import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";

// icons
import {
  Search,
  Plus,
  Trash2,
  FileEdit,
  CircleCheck,
  CircleX,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";

// react-redux
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  deleteCategory as onDeleteCategory,
  addCategory as onAddCategory,
  getCategories as onGetCategories,
  updateCategory as onUpdateCategory,
} from "slices/thunk";
import { ToastContainer } from "react-toastify";
import filterDataBySearch from "Common/filterDataBySearch";

const Categories = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Jewellery,
    (state) => ({
      categories: state.categories,
      error: state.errors,
      isLoading: state.isLoading,
    })
  );

  const { categories, isLoading } = useSelector(selectDataList);

  const [data, setData] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();

  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [categoryName, setCategoryName] = useState<string>("");

  // const isDuplicate = categories.some((category: any) => category.name.toLowerCase() === categoryName.toLowerCase());

  // Get Data
  useEffect(() => {
    dispatch(onGetCategories());
  }, [dispatch]);

  useEffect(() => {
    setData(categories);
  }, [categories]);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell._id) {
      setEventData(cell);
    }
  };

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeleteCategory(eventData._id));
      setDeleteModal(false);
    }
  };
  //

  // Update Data
  const handleUpdateDataClick = (ele: any) => {
    setCategoryName(ele.name);
    setEventData({ ...ele });
    setIsEdit(true);
    setShow(true);
  };

  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      category: (eventData && eventData.name) || "",
      showRingSize: eventData?.showRingSize || false,
      showBraceletLength: eventData?.showBraceletLength || false,
      showStoneSize: eventData?.showStoneSize || false,
      showBandWidth: eventData?.showBandWidth || false,
    },
    validationSchema: Yup.object({
      category: Yup.string().required("Please Enter Category Name"),
      showRingSize: Yup.boolean(),
      showBraceletLength: Yup.boolean(),
      showStoneSize: Yup.boolean(),
      showBandWidth: Yup.boolean(),
    }),

    onSubmit: (values) => {
      if (isEdit) {
        const updateData = {
          id: eventData._id,
          fields: {
            name: values.category,
            showRingSize: values.showRingSize,
            showBraceletLength: values.showBraceletLength,
            showStoneSize: values.showStoneSize,
            showBandWidth: values.showBandWidth,
          },
        };
        // update user
        dispatch(onUpdateCategory(updateData));
      } else {
        const payload = {
          name: values.category,
          showRingSize: values.showRingSize,
          showBraceletLength: values.showBraceletLength,
          showStoneSize: values.showStoneSize,
          showBandWidth: values.showBandWidth,
        };
        // save new user
        dispatch(onAddCategory(payload));
      }
      toggle();
    },
  });

  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
    } else {
      setShow(true);
      setEventData("");
      validation.resetForm();
    }
  }, [show, validation]);

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ["_id", "name"];
    filterDataBySearch(categories, search, keysToSearch, setData);
  };

  const columns = useMemo(
    () => [
      {
        header: (
          <div className="flex items-center h-full">
            <input
              id="CheckboxAll"
              className="size-4 cursor-pointer bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800"
              type="checkbox"
            />
          </div>
        ),
        enableSorting: false,
        id: "checkAll",
        cell: (cell: any) => {
          return (
            <div className="flex items-center h-full">
              <input
                id={"Checkbox" + cell.row.original.id}
                className="size-4 cursor-pointer bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800"
                type="checkbox"
              />
            </div>
          );
        },
      },
      {
        header: "Category ID",
        accessorKey: "_id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <>
            <Link
              to="#!"
              className="transition-all duration-150 ease-linear order_id text-custom-500 hover:text-custom-600"
            >
              {cell.getValue()}
            </Link>
          </>
        ),
      },
      {
        header: "Category",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Ring Size",
        accessorKey: "showRingSize", // Accessor for showRingSize toggle

        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => boolean }) => (
          <div
            className={`flex justify-center align-center  ${
              getValue() ? "text-green-500" : "text-red-500"
            }`}
          >
            {getValue() ? (
              <CircleCheck className="size-4" />
            ) : (
              <CircleX className="size-4" />
            )}
          </div>
        ),
      },
      {
        header: "Bracelet Len",
        accessorKey: "showBraceletLength", // Accessor for showBraceletLength toggle
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => boolean }) => (
          <div
            className={`flex justify-center align-center  ${
              getValue() ? "text-green-500" : "text-red-500"
            }`}
          >
            {getValue() ? (
              <CircleCheck className="size-4" />
            ) : (
              <CircleX className="size-4" />
            )}
          </div>
        ),
      },
      {
        header: "Stone Size",
        accessorKey: "showStoneSize", // Accessor for showStoneSize toggle
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => boolean }) => (
          <div
            className={`flex justify-center align-center  ${
              getValue() ? "text-green-500" : "text-red-500"
            }`}
          >
            {getValue() ? (
              <CircleCheck className="size-4" />
            ) : (
              <CircleX className="size-4" />
            )}
          </div>
        ),
      },
      {
        header: "Band Width",
        accessorKey: "showBandWidth", // Accessor for showStoneSize toggle
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ getValue }: { getValue: () => boolean }) => (
          <div
            className={`flex justify-center align-center  ${
              getValue() ? "text-green-500" : "text-red-500"
            }`}
          >
            {getValue() ? (
              <CircleCheck className="size-4" />
            ) : (
              <CircleX className="size-4" />
            )}
          </div>
        ),
      },

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex items-center justify-center gap-2">
            <a
              href="#!"
              className="flex items-center justify-center size-8 transition-all duration-150 ease-linear rounded-md bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500"
              onClick={() => {
                const data = cell.row.original;
                handleUpdateDataClick(data);
              }}
            >
              <FileEdit className="size-3"></FileEdit>
            </a>

            <a
              href="#!"
              className="flex items-center justify-center size-8 transition-all duration-150 ease-linear rounded-md bg-red-100 hover:bg-red-200 dark:bg-zink-600 dark:hover:bg-zink-500"
              onClick={() => {
                const data = cell.row.original;
                onClickDelete(data);
              }}
            >
              <Trash2 className="size-3 text-red-500"></Trash2>
            </a>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Order Lists" pageTitle="Ecommerce" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      <ToastContainer closeButton={false} limit={1} />

      <div className="card" id="ordersTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search for ..."
                  autoComplete="off"
                  onChange={(e) => filterSearchData(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="lg:col-span-2 lg:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left">
                <Link
                  to="#!"
                  data-modal-target="addOrderModal"
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={toggle}
                >
                  <Plus className="inline-block size-4" />{" "}
                  <span className="align-middle">Add Category</span>
                </Link>
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="relative flex items-center justify-center min-h-full">
              <div className="flex justify-center fixed top-[50%]">
                <Loader2 className="size-[35px] ltr:mr-2 rtl:ml-2 animate-spin" />
              </div>
            </div>
          ) : (
            <TableContainer
              isPagination={true}
              columns={columns || []}
              data={data || []}
              customPageSize={10}
              divclassName="mt-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="text-center bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 text-center font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
              tdclassName="px-3.5 py-2.5 border-y text-center border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          )}
          {!data?.length && !isLoading && (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  We've searched more than 299+ orders We did not find any
                  orders for you search.
                </p>
              </div>
            </div>
          )}  
        </div>
      </div>

      {/* Order Modal */}

      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {!!isEdit ? "Edit Category" : "Add Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            action="#!"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-12">
                <label
                  htmlFor="categoryNameInput"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Category Name
                </label>
                <input
                  type="text"
                  id="categoryNameInput"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Category name"
                  name="category"
                  onChange={(e) => {
                    validation.handleChange(e);
                    setCategoryName(e.target.value);
                  }}
                  value={validation.values.category || ""}
                />
                {validation.touched.category && validation.errors.category ? (
                  <p className="text-red-400">{validation.errors.category}</p>
                ) : null}
              </div>
              <div className="xl:col-span-12 flex flex-col gap-4">
                {/* Toggle 1 */}
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="showRingSize"
                    checked={validation.values.showRingSize} // Bind the checked state to Formik's state
                    onChange={validation.handleChange}
                    className="sr-only peer"
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    showRingSize
                  </span>
                </label>

                {/* Toggle 2 */}
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="showBraceletLength"
                    checked={validation.values.showBraceletLength} // Bind the checked state to Formik's state
                    onChange={validation.handleChange}
                    className="sr-only peer"
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    showBraceletLength
                  </span>
                </label>

                {/* Toggle 3 */}
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="showStoneSize"
                    checked={validation.values.showStoneSize} // Bind the checked state to Formik's state
                    onChange={validation.handleChange}
                    className="sr-only peer"
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    showStoneSize
                  </span>
                </label>

                {/* Toggle 4 */}
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="showBandWidth"
                    checked={validation.values.showBandWidth} // Bind the checked state to Formik's state
                    onChange={validation.handleChange}
                    className="sr-only peer"
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                    showBandWidth
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="reset"
                className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                onClick={toggle}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-40"
                disabled={
                  isEdit &&
                  categoryName === eventData.name &&
                  validation.values.showRingSize === eventData.showRingSize &&
                  validation.values.showBraceletLength ===
                    eventData.showBraceletLength &&
                  validation.values.showStoneSize === eventData.showStoneSize &&
                  validation.values.showBandWidth === eventData.showBandWidth
                }
              >
                {!!isEdit ? "Update" : "Add Category"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Categories;
